import { useEffect, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import { withNamespaces } from "react-i18next";
import { RiShieldCheckFill } from "react-icons/ri";
import {
    getMeasureTypeBadgeLabel,
} from "../helpers/measureList";
import EvidenceSection from "./EvidenceSection";
import RelatedControlsSection from "./RelatedControlsSection";
import {
    buildUpdateMeasurePayload,
    isAdoptionTypeLocked,
} from "../helpers/measureForm";
import usePositiveListMeasure from "../hooks/usePositiveListMeasure";
import useUpdatePositiveListMeasure from "../hooks/useUpdatePositiveListMeasure";
import MeasureFormFields from "./MeasureFormFields";

const MeasureDetailOffcanvas = ({
    softwareId,
    measureId,
    listTitle,
    isOpen,
    onClose,
    t,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);
    const measureQuery = usePositiveListMeasure({
        measureId: isOpen ? measureId : null,
        t,
    });
    const updateMutation = useUpdatePositiveListMeasure({
        softwareId,
        t,
        onSuccess: () => setIsEditing(false),
    });
    const measure = measureQuery.data;
    const evidence = Array.isArray(measure?.evidence) ? measure.evidence : [];
    const controls = Array.isArray(measure?.controls) ? measure.controls : [];
    const adoptionLocked = isAdoptionTypeLocked(measure);
    const title = measure?.title || listTitle;
    const typeLabel = getMeasureTypeBadgeLabel(measure?.measure_type);

    useEffect(() => {
        setIsEditing(false);
    }, [measureId, isOpen]);

    const saveMeasure = (_event, values) => {
        updateMutation.mutate({
            measureId,
            payload: buildUpdateMeasurePayload(values, measure),
        });
    };

    return (
        <Offcanvas
            className="positive-list-detail"
            show={isOpen}
            onHide={onClose}
            placement="end"
            enforceFocus={!isEvidenceModalOpen}
            restoreFocus={!isEvidenceModalOpen}
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                    <span className="positive-list-detail__heading">
                        <span className="positive-list-detail__shield" aria-hidden="true">
                            <RiShieldCheckFill />
                        </span>
                        <span>
                            <span className="positive-list-detail__name">{title}</span>
                            {typeLabel ? (
                                <span className="positive-list-detail__badge">
                                    {t(typeLabel)}
                                </span>
                            ) : null}
                        </span>
                    </span>
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {measureQuery.isLoading && (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" size="md" />
                    </div>
                )}

                {measureQuery.isError && (
                    <div className="text-danger">
                        {t("An error occurred while fetching the measure.")}
                    </div>
                )}

                {measure && (
                    <>
                        <section className="positive-list-detail__card">
                            <div className="positive-list-detail__card-header">
                                <h6>{t("Basic information")}</h6>
                                {!isEditing && (
                                    <button
                                        type="button"
                                        className="positive-list-detail__icon-button"
                                        aria-label={t("Edit")}
                                        onClick={() => setIsEditing(true)}
                                    >
                                        <i className="ri-pencil-line" />
                                    </button>
                                )}
                            </div>

                            {isEditing ? (
                                <MeasureFormFields
                                    formKey={`edit-measure-${measure.id}`}
                                    splitFields
                                    defaultValues={{
                                        title: measure.title,
                                        measureType: measure.measure_type,
                                        description: measure.description,
                                        adoptionType: measure.adoption_type,
                                    }}
                                    adoptionLocked={adoptionLocked}
                                    isSaving={updateMutation.isLoading}
                                    onSubmit={saveMeasure}
                                    onCancel={() => setIsEditing(false)}
                                    t={t}
                                />
                            ) : (
                                <>
                                    <div className="positive-list-detail__measure-title">
                                        {measure.title}
                                    </div>
                                    {typeLabel ? (
                                        <span className="positive-list-detail__badge">
                                            {t(typeLabel)}
                                        </span>
                                    ) : null}
                                    <div className="positive-list-detail__label">
                                        {t("Description")}
                                    </div>
                                    {measure.description ? (
                                        <p className="positive-list-detail__text mb-0">
                                            {measure.description}
                                        </p>
                                    ) : (
                                        <p className="positive-list-detail__text mb-0">
                                            {t("No description has been added.")}
                                        </p>
                                    )}
                                </>
                            )}
                        </section>

                        <RelatedControlsSection
                            softwareId={softwareId}
                            measureId={measure.id}
                            controls={controls}
                            t={t}
                        />

                        <EvidenceSection
                            softwareId={softwareId}
                            measureId={measure.id}
                            evidence={evidence}
                            onModalChange={setIsEvidenceModalOpen}
                            t={t}
                        />
                    </>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default withNamespaces()(MeasureDetailOffcanvas);
